import React from 'react';
import { Panel, TacticalButton } from './ui/TacticalPrimitives';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'card' | 'chart' | 'metric';
  count?: number;
}

function SkeletonLine({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-[color:rgb(var(--c-text-mute-rgb)/0.1)] rounded-[2px] ${className}`}
      role="presentation"
      aria-hidden="true"
    />
  );
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2" aria-label="Loading content">
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine
          key={i}
          className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`}
        />
      ))}
    </div>
  );
}

export function SkeletonMetricCard() {
  return (
    <Panel className="p-4 bg-cmd-panel animate-pulse" role="presentation">
      <SkeletonLine className="h-3 w-24 mb-3" />
      <SkeletonLine className="h-8 w-16 mb-2" />
      <SkeletonLine className="h-2 w-32" />
    </Panel>
  );
}

export function SkeletonChart() {
  return (
    <Panel className="p-4 bg-cmd-panel animate-pulse" role="presentation">
      <SkeletonLine className="h-3 w-32 mb-4" />
      <div className="flex items-end gap-1 h-40">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 animate-pulse bg-[color:rgb(var(--c-text-mute-rgb)/0.1)] rounded-[2px]"
            style={{ height: `${20 + Math.random() * 60}%` }}
            role="presentation"
            aria-hidden="true"
          />
        ))}
      </div>
    </Panel>
  );
}

export function SkeletonDashboard() {
  return (
    <div className="space-y-6 page-shell" aria-label="Loading dashboard" role="status">
      {/* Header skeleton */}
      <div className="flex items-center gap-4 mb-8">
        <SkeletonLine className="h-10 w-64" />
        <SkeletonLine className="h-10 w-32" />
      </div>
      {/* Metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonMetricCard key={i} />
        ))}
      </div>
      {/* Chart area */}
      <SkeletonChart />
      <span className="sr-only">Loading dashboard data...</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center" role="status">
      {icon && <div className="mb-4 text-cmd-textDim">{icon}</div>}
      <h3 className="text-sm font-semibold text-cmd-text mb-2 uppercase tracking-[0.08em]">{title}</h3>
      <p className="text-xs text-cmd-textDim max-w-sm mb-4">{description}</p>
      {action}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Panel variant="critical" className="flex flex-col items-center justify-center py-12 px-6 text-center" role="alert">
      <div className="w-10 h-10 border border-[color:rgb(var(--c-alert-red-rgb)/0.4)] flex items-center justify-center mb-3 rounded-[2px]">
        <span className="text-red-400 text-lg font-mono">!</span>
      </div>
      <p className="text-xs text-red-300 mb-4 uppercase tracking-[0.08em]">{message}</p>
      {onRetry && (
        <TacticalButton onClick={onRetry} variant="ghost" size="sm">
          Retry
        </TacticalButton>
      )}
    </Panel>
  );
}

export default function SkeletonLoader({ variant = 'text', count = 1 }: SkeletonProps) {
  switch (variant) {
    case 'metric':
      return (
        <>
          {Array.from({ length: count }).map((_, i) => (
            <SkeletonMetricCard key={i} />
          ))}
        </>
      );
    case 'chart':
      return <SkeletonChart />;
    case 'card':
      return (
        <div className="space-y-3">
          {Array.from({ length: count }).map((_, i) => (
            <SkeletonLine key={i} className="h-20 w-full" />
          ))}
        </div>
      );
    default:
      return <SkeletonText lines={count} />;
  }
}
